import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { addItem, updateQty, useCart, cartCount, cartTotal, tableStore, ensureCartRestaurant } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import FoodImg, { sized } from "@/components/FoodImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Search, ShoppingBag, User, Bell, Leaf, Circle, X, Plus, Minus, Flame, Award } from "lucide-react";
import { toast } from "sonner";

export default function CustomerMenu() {
  const { token } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const cart = useCart();
  const [table, setTable] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [waiterOpen, setWaiterOpen] = useState(false);
  const [error, setError] = useState(null);
  // "invalid" = the server looked and this QR / table really is bad.
  // "transient" = we could not reach the server (outage, flaky network); never
  // blame the QR for that, retry quietly first, then offer a Retry button.
  const [errorKind, setErrorKind] = useState(null);
  const attemptRef = useRef({ token: null, n: 0 }); // silent auto-retries used for this token
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [menuError, setMenuError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    if (attemptRef.current.token !== token) attemptRef.current = { token, n: 0 };
    setError(null); setErrorKind(null); setRestaurant(null); setMenuLoaded(false); setMenuError(false);
    api.get(`/tables/resolve/${token}`).then((r) => {
      if (cancelled) return;
      attemptRef.current.n = 0;
      setTable(r.data.table);
      setRestaurant(r.data.restaurant);
      tableStore.set({ token, table: r.data.table, restaurant: r.data.restaurant });
      const rid = r.data.restaurant?.id;
      // A new restaurant → start a fresh cart (old items aren't on this menu).
      if (ensureCartRestaurant(rid)) toast.message(`Started a fresh cart for ${r.data.restaurant?.name || "this restaurant"}`);
      api.get("/categories", { params: { restaurant_id: rid } }).then((res) => !cancelled && setCats(res.data)).catch(() => {});
      // Track load state so we show item skeletons while loading and a real error
      // (with retry) on failure — never the "nothing matches" empty state prematurely.
      api.get("/menu", { params: { restaurant_id: rid } })
        .then((res) => { if (!cancelled) { setItems(res.data); setMenuLoaded(true); } })
        .catch(() => { if (!cancelled) { setMenuError(true); setMenuLoaded(true); } });
    }).catch((e) => {
      if (cancelled) return;
      const status = e.response?.status;
      if (status && status >= 400 && status < 500) {
        // A definite answer from the server: this QR / table really is bad.
        attemptRef.current.n = 0;
        setErrorKind("invalid");
        setError(e.response?.data?.detail || "This QR code isn't valid or the table is no longer active.");
        return;
      }
      // No response, 5xx or a timeout: the server is briefly unreachable (the
      // 2026-10-07 Atlas blip showed a 500 here as "QR not valid"). Keep the
      // skeleton up and retry quietly (1.5s, then 4s) before admitting it.
      if (attemptRef.current.n < 2) {
        attemptRef.current.n += 1;
        retryTimer = setTimeout(() => setReloadKey((k) => k + 1), attemptRef.current.n === 1 ? 1500 : 4000);
        return;
      }
      setErrorKind("transient");
      setError("We couldn't load the menu right now. Check your connection and try again.");
    });
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [token, reloadKey]);

  const filtered = useMemo(() => {
    let out = items;
    if (activeCat !== "all") out = out.filter((i) => i.category_id === activeCat);
    if (q) out = out.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));
    if (filter === "veg") out = out.filter((i) => i.is_veg);
    if (filter === "nonveg") out = out.filter((i) => !i.is_veg);
    if (filter === "best") out = out.filter((i) => i.is_best_seller);
    if (filter === "chef") out = out.filter((i) => i.is_chef_special);
    return out;
  }, [items, activeCat, q, filter]);

  if (error) return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain grid place-items-center p-8">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-2xl bg-[var(--qd-cream)] grid place-items-center mx-auto mb-4"><X className="w-7 h-7 text-[var(--qd-primary)]" aria-hidden /></div>
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1">{errorKind === "transient" ? "Couldn't load the menu" : "Menu unavailable"}</h1>
        <p className="text-sm text-[var(--qd-muted)] mb-6">{error}</p>
        {errorKind === "transient" ? (
          <Button data-testid="resolve-retry" onClick={() => { attemptRef.current.n = 0; setReloadKey((k) => k + 1); }}
            className="rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Try again</Button>
        ) : (
          <Link to="/"><Button className="rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Go to QRDine home</Button></Link>
        )}
      </div>
    </div>
  );
  if (!restaurant) return <MenuSkeleton />;

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain pb-32">
      {/* Header — compact so dishes are visible in the first screen */}
      <div className="relative h-48 overflow-hidden">
        <img src={sized(restaurant.hero_url, 1200)} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 qd-hero-scrim" />
        <div className="absolute top-0 left-0 right-0 p-5 flex items-center justify-between">
          <Link to="/profile" data-testid="menu-profile-btn" aria-label="Your profile" className="w-11 h-11 rounded-full qd-glass grid place-items-center">
            <User className="w-4 h-4" aria-hidden />
          </Link>
          <div className="text-white text-center">
            <div className="text-[10px] font-mono-alt uppercase tracking-[0.3em] opacity-80">Table</div>
            <div data-testid="menu-table-label" className="font-display font-bold text-lg">{table.label}</div>
          </div>
          <Link to="/cart" data-testid="menu-cart-btn" aria-label={`Cart, ${cartCount()} item${cartCount() === 1 ? "" : "s"}`} className="w-11 h-11 rounded-full qd-glass grid place-items-center relative">
            <ShoppingBag className="w-4 h-4" aria-hidden />
            {cartCount() > 0 && <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[var(--qd-primary)] text-white text-[10px] w-5 h-5 rounded-full grid place-items-center font-bold">{cartCount()}</span>}
          </Link>
        </div>
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <h1 className="font-display text-4xl font-black tracking-tight">{restaurant.name}</h1>
          <p className="text-sm opacity-90 mt-1">{restaurant.tagline}</p>
        </div>
      </div>

      {/* Search */}
      <div className="px-5 mt-6 sticky top-0 z-30 py-3 bg-[var(--qd-bg)]/90 backdrop-blur">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qd-muted)]" aria-hidden />
          <Input data-testid="menu-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chai, snacks…" className="pl-11 h-12 rounded-full bg-white border-[var(--qd-border)]" />
        </div>
        {/* Filters */}
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {[
            { k: "all", l: "All" },
            { k: "veg", l: "Veg", icon: <Leaf className="w-3 h-3" /> },
            { k: "nonveg", l: "Non-veg", icon: <Circle className="w-3 h-3 fill-red-500 text-red-500" /> },
            { k: "best", l: "Best Seller", icon: <Award className="w-3 h-3" /> },
            { k: "chef", l: "Chef's Special", icon: <Flame className="w-3 h-3" /> },
          ].map((f) => (
            <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)} className={`shrink-0 px-4 h-9 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${filter === f.k ? "bg-[var(--qd-text)] text-white border-[var(--qd-text)]" : "bg-white text-[var(--qd-text)] border-[var(--qd-border)]"}`}>
              {f.icon}{f.l}
            </button>
          ))}
        </div>
        {/* Category rail */}
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          <CatPill active={activeCat === "all"} onClick={() => setActiveCat("all")} label="All" icon="✨" testid="cat-all" />
          {cats.map((c) => (
            <CatPill key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} label={c.name} icon={c.icon} testid={`cat-${c.slug}`} />
          ))}
        </div>
      </div>

      {/* Grid */}
      {menuError ? (
        <div className="px-5 mt-10 text-center" data-testid="menu-error">
          <div className="text-4xl" aria-hidden>😕</div>
          <p className="mt-3 text-sm text-[var(--qd-muted)]">We couldn't load the menu. Check your connection and try again.</p>
          <button data-testid="menu-retry" onClick={() => setReloadKey((k) => k + 1)} className="mt-4 inline-flex items-center justify-center h-11 px-6 rounded-full bg-[var(--qd-primary)] text-white text-sm font-semibold">Retry</button>
        </div>
      ) : (
      <div className="px-5 mt-4 grid grid-cols-2 gap-4">
        {!menuLoaded && [1, 2, 3, 4].map((k) => <ItemSkeleton key={`sk-${k}`} />)}
        {menuLoaded && filtered.map((it, i) => (
          <motion.div
            key={it.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i, 10) * 0.03 }}
            className="bg-white rounded-2xl overflow-hidden qd-shadow-soft border border-[var(--qd-border)]"
          >
            <button data-testid={`menu-item-${it.id}`} onClick={() => setSelected(it)} aria-label={`View ${it.name}`} className="block w-full text-left">
              <div className="aspect-square relative">
                <FoodImg src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {it.is_veg ? <VegDot veg /> : <VegDot />}
                  {it.is_best_seller && <span className="text-[9px] font-bold uppercase tracking-wider bg-[var(--qd-primary)] text-white px-1.5 py-0.5 rounded">Best</span>}
                  {it.is_chef_special && <span className="text-[9px] font-bold uppercase tracking-wider bg-[var(--qd-secondary)] text-white px-1.5 py-0.5 rounded">Chef</span>}
                </div>
              </div>
              <div className="px-3 pt-3">
                <div className="font-semibold text-sm leading-snug line-clamp-2">{it.name}</div>
              </div>
            </button>
            {/* flex-wrap: on 375px 2-col cards the stepper otherwise squeezes over the price.
                The price div must be shrink-0 (NOT min-w-0): a shrinkable child lets flex
                crush it to nothing instead of wrapping, so the text overflowed the stepper. */}
            <div className="px-3 pb-3 mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="shrink-0">
                <span className="font-display font-bold">{formatMoney(it.price)}</span>
                {(it.modifier_groups || []).length > 0 && (
                  <span className="block text-[9px] font-mono-alt uppercase tracking-wider text-[var(--qd-muted)]">Customisable</span>
                )}
              </div>
              <QuickAdd item={it} cart={cart} onCustomize={() => setSelected(it)} />
            </div>
          </motion.div>
        ))}
        {menuLoaded && filtered.length === 0 && (
          <div data-testid="menu-empty" className="col-span-2 text-center py-16">
            <div className="text-4xl" aria-hidden>🍽️</div>
            <p className="mt-3 text-sm text-[var(--qd-muted)]">Nothing matches{q ? ` “${q}”` : ""} here.</p>
            <button data-testid="menu-clear-filters" onClick={() => { setQ(""); setFilter("all"); setActiveCat("all"); }} className="mt-3 text-sm font-semibold text-[var(--qd-primary)]">
              Clear search & filters
            </button>
          </div>
        )}
      </div>
      )}

      {/* Floating Cart CTA */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} className="fixed bottom-20 left-5 right-5 z-40">
            <Link to="/cart" data-testid="floating-cart-btn" className="flex items-center justify-between bg-[var(--qd-text)] text-white p-4 rounded-2xl qd-shadow-lift">
              <span className="font-semibold text-sm inline-flex items-center gap-1.5">
                <motion.span key={cartCount()} initial={{ scale: 1.5 }} animate={{ scale: 1 }} className="inline-block">{cartCount()}</motion.span>
                {cartCount() === 1 ? "item" : "items"} · {formatMoney(cartTotal())}
              </span>
              <span className="text-sm font-semibold inline-flex items-center gap-2">View Cart <ShoppingBag className="w-4 h-4" /></span>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Waiter */}
      <button data-testid="call-waiter-btn" onClick={() => setWaiterOpen(true)} aria-label="Call waiter" className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-white text-[var(--qd-primary)] qd-shadow-lift border border-[var(--qd-border)] grid place-items-center">
        <Bell className="w-5 h-5" aria-hidden />
      </button>

      {/* Product Detail */}
      <ProductSheet item={selected} onClose={() => setSelected(null)} />
      {/* Waiter Sheet */}
      <WaiterSheet open={waiterOpen} onClose={() => setWaiterOpen(false)} token={token} />
    </div>
  );
}

// Swiggy-style one-tap add. Items with modifier groups open the customize sheet;
// simple items add straight to the cart and swap the button for a stepper.
function QuickAdd({ item, cart, onCustomize }) {
  const customizable = (item.modifier_groups || []).length > 0;
  const qty = cart.reduce((s, l) => s + (l.menu_item_id === item.id ? l.quantity : 0), 0);
  const lineIdx = cart.findIndex((l) => l.menu_item_id === item.id);

  const quickAdd = () => {
    addItem({
      menu_item_id: item.id, name: item.name, image_url: item.image_url,
      base_price: item.price, price: item.price, quantity: 1,
      selected_option_ids: [], modifiers: [], notes: null,
    });
  };

  if (customizable || qty === 0) {
    return (
      <button
        data-testid={`quick-add-${item.id}`}
        onClick={customizable ? onCustomize : quickAdd}
        className="relative shrink-0 h-11 px-5 rounded-full border border-[var(--qd-primary)] text-[var(--qd-primary)] text-xs font-bold uppercase tracking-wider bg-white hover:bg-orange-50 transition-colors"
      >
        Add{customizable && qty > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[var(--qd-primary)] text-white text-[9px] grid place-items-center font-bold">{qty}</span>
        )}
      </button>
    );
  }
  return (
    <div className="shrink-0 ml-auto inline-flex items-center gap-0.5 bg-[var(--qd-primary)] text-white rounded-full h-10 px-1">
      <button data-testid={`quick-minus-${item.id}`} aria-label="Remove one" onClick={() => updateQty(lineIdx, qty - 1)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/15"><Minus className="w-4 h-4" aria-hidden /></button>
      <motion.span key={qty} initial={{ scale: 1.4 }} animate={{ scale: 1 }} className="text-base font-bold w-5 text-center">{qty}</motion.span>
      <button data-testid={`quick-plus-${item.id}`} aria-label="Add one" onClick={quickAdd} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/15"><Plus className="w-4 h-4" aria-hidden /></button>
    </div>
  );
}

// A single item-card placeholder shown while the menu list loads (prevents the
// "nothing matches" empty state from flashing before items arrive).
function ItemSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-[var(--qd-border)] overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200" />
      <div className="p-3 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-6 bg-gray-200 rounded w-1/2" /></div>
    </div>
  );
}

// First-seconds skeleton: the page shape appears instantly while data loads.
function MenuSkeleton() {
  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain animate-pulse" data-testid="menu-skeleton">
      <div className="h-48 bg-gray-200" />
      <div className="px-5 mt-6 space-y-3">
        <div className="h-12 bg-gray-200 rounded-full" />
        <div className="flex gap-2">{[1, 2, 3, 4].map((k) => <div key={k} className="h-9 w-24 bg-gray-200 rounded-full shrink-0" />)}</div>
        <div className="grid grid-cols-2 gap-4 pt-2">
          {[1, 2, 3, 4].map((k) => (
            <div key={k} className="bg-white rounded-2xl border border-[var(--qd-border)] overflow-hidden">
              <div className="aspect-square bg-gray-200" />
              <div className="p-3 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-1/3" /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CatPill({ active, onClick, label, icon, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-[var(--qd-primary)] text-white border-[var(--qd-primary)]" : "bg-white text-[var(--qd-text)] border-[var(--qd-border)]"}`}>
      <span aria-hidden>{icon}</span> {label}
    </button>
  );
}

function VegDot({ veg }) {
  return (
    <span className={`w-4 h-4 grid place-items-center border ${veg ? "border-green-600" : "border-red-600"} bg-white rounded`}>
      <span className={`w-2 h-2 rounded-full ${veg ? "bg-green-600" : "bg-red-600"}`} />
    </span>
  );
}

function ProductSheet({ item, onClose }) {
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState({});   // groupId -> [optionId]
  const [custom, setCustom] = useState("");
  useEffect(() => { if (item) { setQty(1); setSel({}); setCustom(""); } }, [item?.id]);
  if (!item) return null;

  const groups = item.modifier_groups || [];
  const chooseSingle = (g, oid) => setSel((s) => ({ ...s, [g.id]: [oid] }));
  const toggleMulti = (g, oid) => setSel((s) => {
    const cur = s[g.id] || [];
    if (cur.includes(oid)) return { ...s, [g.id]: cur.filter((x) => x !== oid) };
    if (g.max && cur.length >= g.max) return s;   // at max — ignore extra taps
    return { ...s, [g.id]: [...cur, oid] };
  });

  const selectedOptionIds = Object.values(sel).flat();
  const allOptions = groups.flatMap((g) => g.options || []);
  const delta = selectedOptionIds.reduce((sum, oid) => {
    const o = allOptions.find((x) => x.id === oid);
    return sum + (o ? Number(o.price_delta) || 0 : 0);
  }, 0);
  const unitPrice = item.price + delta;

  const groupOk = (g) => {
    const count = (sel[g.id] || []).length;
    if (g.type === "single") return g.required ? count === 1 : count <= 1;
    const min = g.required ? Math.max(g.min || 0, 1) : (g.min || 0);
    return count >= min;
  };
  const requiredOk = groups.every(groupOk);

  const add = () => {
    const modifiers = groups.flatMap((g) =>
      (sel[g.id] || []).map((oid) => {
        const o = (g.options || []).find((x) => x.id === oid);
        return { group_name: g.name, option_name: o?.name, price_delta: Number(o?.price_delta) || 0 };
      })
    );
    addItem({
      menu_item_id: item.id, name: item.name, image_url: item.image_url,
      base_price: item.price, price: unitPrice, quantity: qty,
      selected_option_ids: selectedOptionIds, modifiers, notes: custom.trim() || null,
    });
    toast.success(`Added ${qty} × ${item.name}`);
    onClose();
  };

  const groupHint = (g) =>
    g.required ? "Required" : g.type === "multi" ? (g.max ? `Up to ${g.max}` : "Optional") : "Optional";

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-modal" showClose={false} className="max-w-md p-0 overflow-hidden rounded-3xl gap-0 max-h-[90vh] flex flex-col">
        <div className="relative h-40 shrink-0">
          <FoodImg src={item.image_url} alt={item.name} w={800} className="w-full h-full object-cover" />
          <button data-testid="product-close-btn" aria-label="Close" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 grid place-items-center"><X className="w-4 h-4" aria-hidden /></button>
        </div>
        <div className="p-6 space-y-5 overflow-y-auto">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">{item.name}</h2>
            <p className="text-sm text-[var(--qd-muted)] mt-1 leading-relaxed">{item.description}</p>
          </div>

          {groups.map((g) => (
            <div key={g.id} data-testid={`mod-group-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">{g.name}</div>
                <div className={`text-[10px] font-mono-alt uppercase tracking-widest ${g.required && !groupOk(g) ? "text-[var(--qd-primary)]" : "text-[var(--qd-muted)]"}`}>{groupHint(g)}</div>
              </div>
              <div className="space-y-1.5">
                {(g.options || []).map((o) => {
                  const checked = (sel[g.id] || []).includes(o.id);
                  return (
                    <button key={o.id} type="button" data-testid={`opt-${o.id}`}
                      onClick={() => g.type === "single" ? chooseSingle(g, o.id) : toggleMulti(g, o.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-colors ${checked ? "border-[var(--qd-primary)] bg-orange-50" : "border-[var(--qd-border)] bg-white"}`}>
                      <span className="inline-flex items-center gap-2.5">
                        <span className={`w-4 h-4 grid place-items-center border-2 ${g.type === "single" ? "rounded-full" : "rounded"} ${checked ? "border-[var(--qd-primary)]" : "border-gray-300"}`}>
                          {checked && <span className={`bg-[var(--qd-primary)] ${g.type === "single" ? "w-2 h-2 rounded-full" : "w-2.5 h-2.5 rounded-[3px]"}`} />}
                        </span>
                        {o.name}
                      </span>
                      {Number(o.price_delta) > 0 && <span className="text-xs text-[var(--qd-muted)]">+₹{o.price_delta}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <div className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)] mb-2">Special instructions</div>
            <Textarea data-testid="product-custom-note" value={custom} maxLength={200} onChange={(e) => setCustom(e.target.value)} placeholder="Anything else? (e.g. no ice)" className="rounded-xl" />
          </div>
        </div>

        <div className="p-4 border-t border-[var(--qd-border)] flex items-center justify-between gap-3 shrink-0 bg-white">
          <div className="inline-flex items-center gap-3 bg-[var(--qd-cream)] rounded-full p-1">
            <button data-testid="qty-minus" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full bg-white grid place-items-center"><Minus className="w-4 h-4" aria-hidden /></button>
            <span data-testid="qty-value" className="font-bold w-6 text-center">{qty}</span>
            <button data-testid="qty-plus" aria-label="Increase quantity" onClick={() => setQty(Math.min(20, qty + 1))} className="w-9 h-9 rounded-full bg-white grid place-items-center"><Plus className="w-4 h-4" aria-hidden /></button>
          </div>
          <Button data-testid="add-to-cart-btn" disabled={!requiredOk} onClick={add}
            className="flex-1 h-12 rounded-full px-6 bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white font-semibold disabled:opacity-50">
            {requiredOk ? `Add · ₹${(unitPrice * qty).toFixed(0)}` : "Select required options"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WaiterSheet({ open, onClose, token }) {
  const [type, setType] = useState(null);
  const submit = async (t) => {
    setType(t);
    try {
      await api.post("/waiter/request", { table_token: token, type: t });
      toast.success("Waiter has been notified");
      onClose();
    } catch { toast.error("Could not call waiter"); }
    setType(null);
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl">
        <SheetHeader><SheetTitle className="font-display text-2xl">Call Waiter</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-6 pb-6">
          {[
            { k: "water", l: "Need Water" },
            { k: "bill", l: "Need Bill" },
            { k: "cutlery", l: "Cutlery" },
            { k: "assistance", l: "Assistance" },
          ].map((o) => (
            <button key={o.k} data-testid={`waiter-${o.k}`} disabled={type === o.k} onClick={() => submit(o.k)} className="h-16 rounded-2xl border border-[var(--qd-border)] bg-white font-semibold text-sm hover:bg-[var(--qd-cream)] transition-colors">
              {o.l}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
